import { useEffect, useState } from 'react';
import { auth, db } from '../../config/firebase';
import { get, ref, onValue } from 'firebase/database';
import { CiExport } from "react-icons/ci";
import { SlRefresh } from "react-icons/sl";
import { MdClear } from "react-icons/md";
import { MdArrowDropDown } from "react-icons/md";
import { IoMdCheckmark } from "react-icons/io";
import { BiSolidChevronUp, BiSolidChevronDown } from "react-icons/bi";


import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import clsx from 'clsx';
import Modal from '../../reusables/Modal';
import LoadingSpinner from '../shared/LoadingSpinner';







function UserLogsViewer() {
  const [logs, setLogs] = useState([]);
  const [filterUser, setFilterUser] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [uniqueEvents, setUniqueEvents] = useState([]);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage, setLogsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [userMetadata, setUserMetadata] = useState({});
  const [companyNames, setCompanyNames] = useState({});
  const [selectedLog, setSelectedLog] = useState(null);
  const [loading, setLoading] = useState(true);
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });

  // Build a compact pagination window like: 1 … 8 9 [10] 11 12 … 40
  const getVisiblePages = (current, total, neighbors = 2) => {
    const pages = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }
    const start = Math.max(2, current - neighbors);
    const end = Math.min(total - 1, current + neighbors);
    pages.push(1);
    if (start > 2) pages.push('…');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push('…');
    pages.push(total);
    return pages;
  };
  // Date range state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const refreshLogs = () => {
    setLoading(true);
    const logsRef = ref(db, 'userLogs');
    get(logsRef).then(async (snapshot) => {
      if (!snapshot.exists()) {
        setLogs([]);
        setLoading(false);
        return;
      }
      const data = snapshot.val();
      const formatted = [];
      const uidsToFetch = new Set();

      Object.entries(data).forEach(([uid, userLogs]) => {
        Object.entries(userLogs).forEach(([logId, log]) => {
          formatted.push({ uid, ...log });
          uidsToFetch.add(uid);
        });
      });

      formatted.sort((a, b) => b.timestamp - a.timestamp);
      setLogs(formatted);

      const users = Array.from(uidsToFetch);
      setUniqueUsers(users);

      const events = new Set();
      formatted.forEach(log => {
        if (log.event) events.add(log.event);
      });
      setUniqueEvents(Array.from(events));

      const metadata = {};
      for (const uid of users) {
        const userSnap = await get(ref(db, `users/${uid}`));
        if (userSnap.exists()) {
          const userData = userSnap.val();
          metadata[uid] = {
            email: userData.email || '',
            role: userData.role || '',
            companyId: userData.companyId || ''
          };

          if (userData.companyId) {
            const companySnap = await get(ref(db, `companies/${userData.companyId}/name`));
            if (companySnap.exists()) {
              setCompanyNames(prev => ({
                ...prev,
                [userData.companyId]: companySnap.val()
              }));
            }
          }
        }
      }
      setUserMetadata(metadata);
      setLoading(false);
    });
  };

  useEffect(() => {
    const logsRef = ref(db, 'userLogs');
    const unsubscribe = onValue(logsRef, async (snapshot) => {
      setLoading(true);
      if (!snapshot.exists()) {
        setLoading(false);
        return;
      }
      const data = snapshot.val();
      const formatted = [];
      const uidsToFetch = new Set();

      Object.entries(data).forEach(([uid, userLogs]) => {
        Object.entries(userLogs).forEach(([logId, log]) => {
          formatted.push({ uid, ...log });
          uidsToFetch.add(uid);
        });
      });

      formatted.sort((a, b) => b.timestamp - a.timestamp);
      setLogs(formatted);

      const users = Array.from(uidsToFetch);
      setUniqueUsers(users);

      const events = new Set();
      formatted.forEach(log => {
        if (log.event) events.add(log.event);
      });
      setUniqueEvents(Array.from(events));

      // Fetch user metadata
      const metadata = {};
      await Promise.all(users.map(async uid => {
        const userSnap = await get(ref(db, `users/${uid}`));
        if (userSnap.exists()) {
          const userData = userSnap.val();
          metadata[uid] = {
            email: userData.email || '',
            role: userData.role || '',
            companyId: userData.companyId || ''
          };

          // Fetch company name if companyId exists
          if (userData.companyId) {
            const companySnap = await get(ref(db, `companies/${userData.companyId}/name`));
            if (companySnap.exists()) {
              setCompanyNames(prev => ({
                ...prev,
                [userData.companyId]: companySnap.val()
              }));
            }
          }
        }
      }));
      setUserMetadata(metadata);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Compute filtered logs
  const filteredLogs = logs.filter(log => {
    const metadata = userMetadata[log.uid] || {};
    return (
      (!startDate || new Date(log.timestamp) >= new Date(startDate)) &&
      (!endDate || new Date(log.timestamp) <= new Date(endDate)) &&
      (!filterUser || log.uid.includes(filterUser)) &&
      (!filterEvent || log.event?.toLowerCase().includes(filterEvent.toLowerCase())) &&
      (!filterRole || metadata.role === filterRole) &&
      (!filterCompany || companyNames[metadata.companyId] === filterCompany) &&
      (!searchTerm ||
        Object.entries({
          ...log,
          ...userMetadata[log.uid],
          companyName: companyNames[userMetadata[log.uid]?.companyId]
        })
          .some(([key, val]) => val?.toString().toLowerCase().includes(searchTerm.toLowerCase())))
    );
  });
  // Sorting and pagination
  const sortedLogs = [...filteredLogs].sort((a, b) => {
    const key = sortConfig.key;
    const aVal =
      key === 'role'
        ? userMetadata[a.uid]?.role ?? ''
        : key === 'companyName'
          ? companyNames[userMetadata[a.uid]?.companyId] ?? ''
          : key === 'uid'
            ? userMetadata[a.uid]?.email ?? a.uid
            : a[key] ?? '';
    const bVal =
      key === 'role'
        ? userMetadata[b.uid]?.role ?? ''
        : key === 'companyName'
          ? companyNames[userMetadata[b.uid]?.companyId] ?? ''
          : key === 'uid'
            ? userMetadata[b.uid]?.email ?? b.uid
            : b[key] ?? '';
    const aStr = typeof aVal === 'string' ? aVal.toLowerCase() : aVal;
    const bStr = typeof bVal === 'string' ? bVal.toLowerCase() : bVal;
    if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
  const displayedLogs = logsPerPage === filteredLogs.length
    ? sortedLogs
    : sortedLogs.slice((currentPage - 1) * logsPerPage, currentPage * logsPerPage);
  const totalPages = logsPerPage === filteredLogs.length
    ? 1
    : Math.ceil(filteredLogs.length / logsPerPage);
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filterUser, filterEvent, filterRole, filterCompany, searchTerm, logsPerPage, startDate, endDate]);

  return (
    <div className="p-6">
      {loading ? (
        <LoadingSpinner
          text="Loading logs..."
          size="md"
          className=" h-auto "
        />
      ) : (
        <>
          <div className='flex justify-between items-center mb-4'>
            <div className="mb-4">
              <label className="block text-sm ">Search Logs:</label>
              <input
                type="text"
                className="border px-2 py-1 rounded h-10 w-80 text-sm border-gray-300 mt-1"
                placeholder="Search by UID or Event..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className='flex items-center gap-2'>
              {(filterUser || filterEvent || filterRole || filterCompany || searchTerm || startDate || endDate) && (
                <div
                  onClick={() => {
                    setFilterUser('');
                    setFilterEvent('');
                    setFilterRole('');
                    setFilterCompany('');
                    setSearchTerm('');
                    setStartDate('');
                    setEndDate('');
                  }}
                  className="bg-red-200 text-red-700 px-3 py-1 rounded hover:bg-red-300 h-10 items-center flex cursor-pointer"
                >
                  <MdClear className="mr-2" />
                  <span>Clear Filters</span>
                </div>
              )}

              <div
                onClick={refreshLogs}
                className="bg-green-200 text-green-700 px-3 py-1 rounded hover:bg-green-300 h-10 items-center flex cursor-pointer"
              >
                <SlRefresh className="mr-2" />
                <span>Refresh</span>
              </div>

              <div
                onClick={() => {
                  const csvRows = [];

                  // Define a schema per event type
                  const eventSchemas = {
                    upload_std_files: ['fileCount', 'fileNames', 'priorityOrder'],
                    login: ['device', 'ip'],
                    // Add more schemas here as needed
                  };

                  // Standard headers
                  const headers = [
                    'uid',
                    'email',
                    'role',
                    'companyName',
                    'event',
                    'timestamp',
                    ...Array.from(new Set(filteredLogs.flatMap(log => eventSchemas[log.event] || [])))
                  ];
                  csvRows.push(headers.join(','));

                  filteredLogs.forEach(log => {
                    const user = userMetadata[log.uid] || {};
                    const row = headers.map(header => {
                      if (header === 'uid') return `"${log.uid}"`;
                      if (header === 'email') return `"${user.email || ''}"`;
                      if (header === 'role') return `"${user.role || ''}"`;
                      if (header === 'companyName') return `"${companyNames[user.companyId] || ''}"`;
                      if (header === 'event') return `"${log.event || ''}"`;
                      if (header === 'timestamp') return `"${new Date(log.timestamp).toISOString()}"`;
                      return `"${String(log[header] ?? log.details?.[header] ?? '').replace(/"/g, '""')}"`;
                    });
                    csvRows.push(row.join(','));
                  });

                  const csvData = new Blob([csvRows.join('\n')], { type: 'text/csv' });
                  const url = URL.createObjectURL(csvData);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'logs_export.csv';
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                className="bg-blue-200 text-blue-700 px-3 py-1 rounded hover:bg-blue-300 h-10 items-center flex cursor-pointer justify-center">
                <CiExport className="mr-2" />
                <span>Export</span>
              </div>
            </div>
          </div>


          <div className="mb-4 flex gap-4 items-end">
            <div>
              <label className="block text-sm mb-1">Filter by UID:</label>
              <Listbox value={filterUser} onChange={setFilterUser}>
                <div className="relative w-64">
                  <ListboxButton
                    className={clsx(
                      "relative w-full cursor-default rounded-md py-2 pl-3 pr-14 text-left text-sm border focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 truncate overflow-hidden",
                      filterUser
                        ? "bg-blue-50 border-blue-500 text-blue-700"
                        : "bg-white border-gray-300 text-gray-900"
                    )}
                  >
                    {filterUser || 'All Users'}
                    <MdArrowDropDown
                      className="absolute inset-y-0 right-3 my-auto h-5 w-5 text-gray-400 pointer-events-none"
                      aria-hidden="true"
                    />
                  </ListboxButton>
                  <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <ListboxOption
                      key="all"
                      value=""
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                        }`
                      }
                    >
                      All Users
                    </ListboxOption>
                    {uniqueUsers.map(uid => (
                      <ListboxOption
                        key={uid}
                        value={uid}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {uid}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                <IoMdCheckmark className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            </div>
            <div>
              <label className="block text-sm mb-1">Filter by Event:</label>
              <Listbox value={filterEvent} onChange={setFilterEvent}>
                <div className="relative w-64">
                  <ListboxButton
                    className={clsx(
                      "relative w-full cursor-default rounded-md py-2 pl-3 pr-14 text-left text-sm border focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 truncate overflow-hidden",
                      filterEvent
                        ? "bg-blue-50 border-blue-500 text-blue-700"
                        : "bg-white border-gray-300 text-gray-900"
                    )}
                  >
                    {filterEvent || 'All Events'}
                    <MdArrowDropDown
                      className="absolute inset-y-0 right-3 my-auto h-5 w-5 text-gray-400 pointer-events-none"
                      aria-hidden="true"
                    />
                  </ListboxButton>
                  <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <ListboxOption
                      key="all"
                      value=""
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                        }`
                      }
                    >
                      All Events
                    </ListboxOption>
                    {uniqueEvents.map(event => (
                      <ListboxOption
                        key={event}
                        value={event}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {event}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                <IoMdCheckmark className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            </div>
            <div>
              <label className="block text-sm mb-1">Filter by Role:</label>
              <Listbox value={filterRole} onChange={setFilterRole}>
                <div className="relative w-64">
                  <ListboxButton
                    className={clsx(
                      "relative w-full cursor-default rounded-md py-2 pl-3 pr-14 text-left text-sm border focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 truncate overflow-hidden",
                      filterRole
                        ? "bg-blue-50 border-blue-500 text-blue-700"
                        : "bg-white border-gray-300 text-gray-900"
                    )}
                  >
                    {filterRole || 'All Roles'}
                    <MdArrowDropDown
                      className="absolute inset-y-0 right-3 my-auto h-5 w-5 text-gray-400 pointer-events-none"
                      aria-hidden="true"
                    />
                  </ListboxButton>
                  <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <ListboxOption
                      key="all"
                      value=""
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                        }`
                      }
                    >
                      All Roles
                    </ListboxOption>
                    {Array.from(new Set(Object.values(userMetadata).map(u => u.role).filter(Boolean))).map(role => (
                      <ListboxOption
                        key={role}
                        value={role}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {role}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                <IoMdCheckmark className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            </div>
            <div>
              <label className="block text-sm mb-1">Filter by Company:</label>
              <Listbox value={filterCompany} onChange={setFilterCompany}>
                <div className="relative w-64">
                  <ListboxButton
                    className={clsx(
                      "relative w-full cursor-default rounded-md py-2 pl-3 pr-14 text-left text-sm border focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 truncate overflow-hidden",
                      filterCompany
                        ? "bg-blue-50 border-blue-500 text-blue-700"
                        : "bg-white border-gray-300 text-gray-900"
                    )}
                  >
                    {filterCompany || 'All Companies'}
                    <MdArrowDropDown
                      className="absolute inset-y-0 right-3 my-auto h-5 w-5 text-gray-400 pointer-events-none"
                      aria-hidden="true"
                    />
                  </ListboxButton>
                  <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <ListboxOption
                      key="all"
                      value=""
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                        }`
                      }
                    >
                      All Companies
                    </ListboxOption>
                    {Array.from(new Set(Object.values(userMetadata).map(u => companyNames[u.companyId]).filter(Boolean))).map(name => (
                      <ListboxOption
                        key={name}
                        value={name}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {name}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                <IoMdCheckmark className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              </Listbox>
            </div>
          </div>

          {/* Date range filters in their own row */}
          <div className="mb-6 flex gap-4 items-end">
            <div>
              <label className="block text-sm mb-1">Start Date:</label>
              <input
                type="datetime-local"
                className={clsx(
                  "rounded-md w-64 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400",
                  startDate
                    ? "bg-blue-50 border-blue-500 text-blue-700"
                    : "bg-white border border-gray-300 text-gray-700"
                )}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm mb-1">End Date:</label>
              <input
                type="datetime-local"
                className={clsx(
                  "rounded-md w-64 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400",
                  endDate
                    ? "bg-blue-50 border-blue-500 text-blue-700"
                    : "bg-white border border-gray-300 text-gray-700"
                )}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {filteredLogs.length > 0 ? (
            <>
              <table className="border-collapse w-full mt-10">
                <thead>
                  <tr className="bg-gray-200">
                    <th
                      className="bg-customeBlack text-white border-white border cursor-pointer"
                      onClick={() => requestSort('uid')}
                    >
                      <span className="flex items-center gap-1">
                        User
                        {sortConfig.key === 'uid' ? (
                          sortConfig.direction === 'asc'
                            ? <BiSolidChevronUp className="w-4 h-4" />
                            : <BiSolidChevronDown className="w-4 h-4" />
                        ) : null}
                      </span>
                    </th>
                    <th
                      className="bg-customeBlack text-white border-white border cursor-pointer "
                      onClick={() => requestSort('role')}
                    >
                      <span className="flex items-center gap-1">
                        Role
                        {sortConfig.key === 'role' ? (
                          sortConfig.direction === 'asc'
                            ? <BiSolidChevronUp className="w-4 h-4" />
                            : <BiSolidChevronDown className="w-4 h-4" />
                        ) : null}
                      </span>
                    </th>
                    <th
                      className="bg-customeBlack text-white border-white border cursor-pointer "
                      onClick={() => requestSort('companyName')}
                    >
                      <span className="flex items-center gap-1">
                        Company
                        {sortConfig.key === 'companyName' ? (
                          sortConfig.direction === 'asc'
                            ? <BiSolidChevronUp className="w-4 h-4" />
                            : <BiSolidChevronDown className="w-4 h-4" />
                        ) : null}
                      </span>
                    </th>
                    <th
                      className="bg-customeBlack text-white border-white border cursor-pointer "
                      onClick={() => requestSort('event')}
                    >
                      <span className="flex items-center gap-1">
                        Event
                        {sortConfig.key === 'event' ? (
                          sortConfig.direction === 'asc'
                            ? <BiSolidChevronUp className="w-4 h-4" />
                            : <BiSolidChevronDown className="w-4 h-4" />
                        ) : null}
                      </span>
                    </th>
                    <th
                      className="bg-customeBlack text-white border-white border cursor-pointer "
                      onClick={() => requestSort('timestamp')}
                    >
                      <span className="flex items-center gap-1">
                        Time
                        {sortConfig.key === 'timestamp' ? (
                          sortConfig.direction === 'asc'
                            ? <BiSolidChevronUp className="w-4 h-4" />
                            : <BiSolidChevronDown className="w-4 h-4" />
                        ) : null}
                      </span>
                    </th>
                    <th className="bg-customeBlack text-white border-white border cursor-pointer ">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedLogs.map((log, index) => (
                    <tr key={index} className="border-t even:bg-gray-100 hover:bg-gray-200">
                      <td className="border p-2">
                        {userMetadata[log.uid]?.email || log.uid}
                      </td>
                      <td className="border p-2">
                        {userMetadata[log.uid]?.role || '-'}
                      </td>
                      <td className="border p-2">
                        {userMetadata[log.uid]?.companyId
                          ? companyNames[userMetadata[log.uid]?.companyId] || userMetadata[log.uid]?.companyId
                          : '-'}
                      </td>
                      <td className="border p-2">{log.event}</td>
                      <td className="border p-2">{new Date(log.timestamp).toLocaleString()}</td>
                      <td className="border p-2 text-center">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="text-blue-600 hover:underline"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-6 gap-4 ">
                <div className="flex justify-center space-x-2 items-center">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded text-white bg-customeBlack disabled:opacity-40"
                  >
                    Previous
                  </button>
                  {getVisiblePages(currentPage, totalPages).map((page, idx) => (
                    page === '…' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 select-none">…</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded ${page === currentPage ? 'bg-customeBlack text-white' : ' text-gray-600 bg-gray-200'}`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                  <div className="ml-4 flex items-center gap-2 text-sm">
                    <label htmlFor="goto-page">Page</label>
                    <input
                      id="goto-page"
                      type="number"
                      min={1}
                      max={totalPages}
                      defaultValue={currentPage}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const val = Math.max(1, Math.min(Number(e.currentTarget.value || 1), totalPages));
                          setCurrentPage(val);
                        }
                      }}
                      onBlur={(e) => {
                        const val = Math.max(1, Math.min(Number(e.currentTarget.value || 1), totalPages));
                        if (val !== currentPage) setCurrentPage(val);
                      }}
                      className="w-16 border border-gray-300 rounded px-2 py-1"
                    />
                    <span className="text-gray-500">/ {totalPages}</span>
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded text-white bg-customeBlack disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
                <div className="flex items-center space-x-2 justify-end">
                  <label className="text-sm ">Logs per page:</label>
                  <Listbox value={logsPerPage} onChange={setLogsPerPage}>
                    <div className="relative w-24">
                      <ListboxButton
                        className={clsx(
                          "relative w-full cursor-default rounded-md py-2 pl-3 pr-10 text-left text-sm border focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 truncate overflow-hidden",
                          logsPerPage !== 5 && logsPerPage !== 10
                            ? "bg-blue-50 border-blue-500 text-blue-700"
                            : "bg-white border-gray-300 text-gray-900"
                        )}
                      >
                        {logsPerPage === filteredLogs.length
                          ? 'All'
                          : logsPerPage}
                        <MdArrowDropDown
                          className="absolute inset-y-0 right-2 my-auto h-5 w-5 text-gray-400 pointer-events-none"
                          aria-hidden="true"
                        />
                      </ListboxButton>
                      <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        <ListboxOption
                          key={5}
                          value={5}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                5
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                  <IoMdCheckmark className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </ListboxOption>
                        <ListboxOption
                          key={10}
                          value={10}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                10
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                  <IoMdCheckmark className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </ListboxOption>
                        <ListboxOption
                          key="all"
                          value={filteredLogs.length}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                All
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                  <IoMdCheckmark className="h-5 w-5" aria-hidden="true" />
                                </span>
                              ) : null}
                            </>
                          )}
                        </ListboxOption>
                      </ListboxOptions>
                    </div>
                  </Listbox>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500 mt-10">
              No logs found. Try adjusting your filters or search.
            </div>
          )}

          <Modal open={!!selectedLog} onClose={() => setSelectedLog(null)}>
            <h2 className="text-2xl  mb-8">Log Details</h2>
            <table className="border-collapse w-full">
              <thead>
                <tr className="bg-customeBlack text-white border-white border">
                  <th className="p-2 text-left">Key</th>
                  <th className="p-2 text-left">Value</th>
                </tr>
              </thead>
              <tbody>
                {selectedLog &&
                  Object.entries(selectedLog).map(([key, value]) => (
                    <tr key={key} className="border-t">
                      <td className="p-2 font-medium">{key}</td>
                      <td className="p-2 whitespace-pre-wrap">
                        {typeof value === 'object' && value !== null ? (
                          <table className="table-auto w-full text-xs border border-gray-300">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-300">
                                <th className="p-1 text-left">Key</th>
                                <th className="p-1 text-left">Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(value).map(([subKey, subVal]) => (
                                <tr key={subKey} className="border-t">
                                  <td className="p-1">{subKey}</td>
                                  <td className="p-1 whitespace-pre-wrap">{String(subVal)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          String(value)
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Modal>
        </>
      )}
    </div>
  );
}

export default UserLogsViewer;